import { LegalPageLayout, LegalSection, LegalList, MailLink } from './LegalPageLayout'

/**
 * Mirrors docs/legal/privacy-policy-draft.md. Keep the two in sync — the markdown file is the
 * source of record for legal review; this page is how it actually reaches users.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" effectiveDate="9 September 2026">
      <p>
        LogMySpends (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) is operated by
        LogMySpends, based in Hyderabad, Telangana, India. This Privacy Policy explains what
        personal data LogMySpends collects when you use the LogMySpends web application (the
        &ldquo;Service&rdquo;), why, who we share it with, and the rights you have over it, in
        accordance with India&rsquo;s Digital Personal Data Protection Act, 2023 (&ldquo;DPDP
        Act&rdquo;).
      </p>
      <p>
        By creating a LogMySpends account, you agree to this Privacy Policy. If you don&rsquo;t
        agree, please don&rsquo;t use the Service.
      </p>

      <LegalSection number={1} title="What we collect">
        <p>
          <strong>Account data</strong> (via Clerk, our authentication provider): your name, email
          address, and a hashed password or social-login identifier. We never see or store your raw
          password.
        </p>
        <p>
          <strong>Household and financial data you enter yourself:</strong> LogMySpends does{' '}
          <strong>not</strong> link to your bank accounts or read your real transactions
          automatically. Every transaction, account balance, budget, goal, loan, and recurring bill
          in the app is something you typed in. This data is, by its nature, sensitive financial
          information, even though it&rsquo;s self-reported rather than pulled from a bank.
        </p>
        <p>
          <strong>Household member data:</strong> if you invite other people into your household —
          including, in some cases, <strong>children or other non-earning family members</strong> —
          their name and email are stored, and any financial data associated with them (e.g. an
          allowance transaction, a goal in their name) is processed the same way as any other
          household data. <strong>We do not knowingly collect data directly from children</strong> —
          a parent or guardian household Owner is responsible for any minor&rsquo;s data entered
          into the app, and the DPDP Act&rsquo;s specific rules on children&rsquo;s personal data
          (verifiable parental consent, no behavioural tracking or targeted advertising to children)
          apply to that data.
        </p>
        <p>
          <strong>Billing data:</strong> when you subscribe to a paid plan, payment is handled
          entirely by <strong>Razorpay</strong> — we never see or store your card number, UPI PIN,
          or bank credentials. We do store: which plan you&rsquo;re on, subscription status, amounts
          charged, payment timestamps, and Razorpay&rsquo;s own transaction/subscription reference
          IDs, so we can show you your billing history and resolve support requests.
        </p>
        <p>
          <strong>Technical data:</strong> IP address, browser/device information, and error
          reports, collected automatically to keep the Service secure and diagnose bugs (via
          Sentry).
        </p>
        <p>
          <strong>Product-analytics data:</strong> which pages you visit and which features you use
          (via <strong>PostHog</strong>), so we can see what&rsquo;s working and what isn&rsquo;t.
          These events are tied only to your account ID once you&rsquo;re signed in &mdash; never
          your name, email, or any financial figure &mdash; and use no advertising or third-party
          tracking cookies (local storage only).
        </p>
        <p>
          <strong>Communications:</strong> if you email support or a Grievance Officer, we keep that
          correspondence to respond to and document the request.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Why we collect it">
        <LegalList
          items={[
            "To provide the Service — you can't use a budgeting app without storing your budget",
            'To process payments and manage your subscription',
            'To send transactional email (invite emails, receipts, trial/billing notices) via Resend',
            'To detect, diagnose, and fix bugs and security issues',
            'To understand which features are used and improve the product (privacy-preserving analytics)',
            'To comply with our own legal and tax obligations',
            'To respond to support requests and DPDP Act rights requests',
          ]}
        />
        <p>
          <strong>
            We do not sell your personal data. We do not use your financial data for advertising,
            and we do not show ads.
          </strong>
        </p>
      </LegalSection>

      <LegalSection number={3} title="Who we share data with">
        <p>
          We share data only with the service providers (&ldquo;Data Processors&rdquo;) necessary to
          run LogMySpends, each under their own privacy/security terms, and only the minimum data
          each needs to do its job:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--fl-border)] text-left">
                <th className="py-2 pr-4 font-semibold text-[var(--fl-heading)]">Provider</th>
                <th className="py-2 pr-4 font-semibold text-[var(--fl-heading)]">
                  What they process
                </th>
                <th className="py-2 font-semibold text-[var(--fl-heading)]">Why</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Clerk', 'Email, authentication credentials, session data', 'Sign-in/sign-up'],
                [
                  'Razorpay',
                  'Name, email, phone, payment method details, amounts',
                  'Payment processing (an RBI-authorised Payment Aggregator; card/UPI details never reach our servers)',
                ],
                ['Resend', 'Email address, email content', 'Transactional email'],
                ['Sentry', 'IP address, device info, application error data', 'Error monitoring'],
                [
                  'PostHog (US)',
                  'Account ID, pages visited, feature-usage events (no name, email, or amounts)',
                  'Product analytics',
                ],
                ['Railway / Cloudflare', 'All of the above, as infrastructure', 'Hosting'],
              ].map(([provider, what, why]) => (
                <tr key={provider} className="border-b border-[var(--fl-divider)]">
                  <td className="py-2 pr-4 align-top font-medium text-[var(--fl-text-1)]">
                    {provider}
                  </td>
                  <td className="py-2 pr-4 align-top">{what}</td>
                  <td className="py-2 align-top">{why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          We do not otherwise share, rent, or sell your data to third parties. We disclose data to
          law enforcement or regulators only where legally compelled to do so.
        </p>
      </LegalSection>

      <LegalSection number={4} title="Where your data is stored">
        <p>
          Our database is hosted on Railway in the United States, and some of the third-party
          processors listed above also operate outside India. Your personal data is therefore
          transferred outside India (a cross-border transfer). The DPDP Act permits such transfers
          to most countries by default, subject to any government-notified restrictions on specific
          countries.
        </p>
      </LegalSection>

      <LegalSection number={5} title="How long we keep it">
        <p>
          We keep your account and financial data for as long as your account is active. If you
          delete your account, we delete your personal data within 30 days except where we&rsquo;re
          legally required to retain billing/tax records for longer. Household financial data
          belonging to other members is retained under the household Owner&rsquo;s account, not
          deleted individually when one member leaves, unless they request their own data be removed
          to the extent it can be separated from shared household records.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Your rights under the DPDP Act">
        <p>You have the right to:</p>
        <LegalList
          items={[
            'Access a summary of the personal data we hold about you',
            'Correct or update inaccurate or incomplete data',
            'Erase your personal data (subject to §5’s exceptions)',
            'Withdraw consent at any time (which may mean we can no longer provide the Service to you)',
            'Nominate another individual to exercise these rights on your behalf in the event of your death or incapacity',
            'File a grievance with our Grievance Officer, and if unresolved, with the Data Protection Board of India',
          ]}
        />
        <p>
          To exercise any of these rights, contact our Grievance Officer (below). We will
          acknowledge your request and respond within a reasonable timeframe as required by the DPDP
          Rules.
        </p>
      </LegalSection>

      <LegalSection number={7} title="Grievance Officer">
        <p>
          For any privacy concern or rights request under the DPDP Act, contact our Grievance
          Officer:
        </p>
        <p>
          <strong>Email:</strong> <MailLink address="logmyspends@gmail.com" />
          <br />
          <strong>Address:</strong> Hyderabad, Telangana, India
        </p>
      </LegalSection>

      <LegalSection number={8} title="Security">
        <p>
          We use industry-standard measures to protect your data: encrypted connections (TLS)
          between your browser and our servers, encrypted database connections, and no storage of
          raw payment credentials (that&rsquo;s Razorpay&rsquo;s PCI-DSS-compliant responsibility,
          not ours). No system is perfectly secure, and we can&rsquo;t guarantee absolute security —
          see the Terms &amp; Conditions for our liability position.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Data breach notification">
        <p>
          In the event of a personal data breach, we will notify the Data Protection Board of India
          and affected users within 72 hours of becoming aware of it, describing what data was
          affected and what steps we&rsquo;re taking. We&rsquo;ll maintain an internal record of any
          breach and our response.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. If we make a material change,
          we&rsquo;ll notify you by email or an in-app notice before it takes effect. Continued use
          of the Service after a change takes effect means you accept the updated policy.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Contact">
        <p>
          Questions about this policy: <MailLink address="logmyspends@gmail.com" />
        </p>
      </LegalSection>
    </LegalPageLayout>
  )
}
